import React, { useCallback, useContext, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import * as Components from "./components/components"
import { ConfigContext } from "./contexts/CustomConfigContext"
import { EventBus } from "./eventbus"
import { CONFIG } from "./services/ConfigService"
import { FeatureService } from "./services/FeatureService"
import { emitRegionChange, useStoreRechnung } from "./store"
import { AnlagenView } from "./views/AnlagenView"
import GesamtsummenView from "./views/Gesamtsummen"
import { LieferinformationView } from "./views/LieferinformationView"
import RechnungErstellenView from "./views/RechnungErstellenView"
import { RechnungsdatenView } from "./views/RechnungsdatenView"
import { RechnungsempfaengerView } from "./views/RechnungsempfaengerView"
import { RechnungsstellerKontaktdatenView } from "./views/RechnungsstellerKontaktdatenView"
import { RechnungsstellerView } from "./views/RechnungsstellerView"
import { RechnungsverlaufView } from "./views/RechnungsverlaufView"
import SteuerDetailsView from "./views/SteuerDetails"
import { SteuervertreterVerkaeufersView } from "./views/SteuervertreterVerkaeufersView"
import VorlageUploadView from "./views/TemplateUploadView"
import { ZahlungsdatenView } from "./views/Zahlungsdaten/ZahlungsdatenView"
import { ZahlungsbedingungenKomplexView } from "./views/Zahlungsdaten/komplex/ZahlungsbedingungenKomplexView"
import { PositionListView } from "./views/positionen/PositionListView"
import { getSteuerberechnungsOption } from "./Utils"

export function AppContainer() {
    const { t } = useTranslation()
    const rechnung = useStoreRechnung("zahlungsdaten", "steuerberechnung")
    const customConfig = useContext(ConfigContext)
    const zahlungsdaten = rechnung.zahlungsdaten

    const onScrollToTopClicked = useCallback(() => {
        EventBus.instance.emit("scroll", "top")
    }, [])

    const onScrollToBottomClicked = useCallback(() => {
        EventBus.instance.emit("scroll", "bottom")
    }, [])

    if (!rechnung.steuerberechnung || rechnung.steuerberechnung.trim() == "") {
        const steuerberechnungField = customConfig?.steuerberechnung
        if (steuerberechnungField && steuerberechnungField.trim() !== "") {
            rechnung.steuerberechnung = getSteuerberechnungsOption(steuerberechnungField)
        } else {
            rechnung.steuerberechnung = getSteuerberechnungsOption(undefined)
        }
        emitRegionChange("steuerberechnung")
    }

    if (!customConfig?.skontoAlsText) {
        // Entfernen von freitexten BT-20 wie in EPE-1217 für DB gefordert
        zahlungsdaten.zahlungsbedingungen = zahlungsdaten.zahlungsbedingungen.filter((item) => item.typ !== undefined)
    } else {
        // für textfall: ändere alle Zeilen in einfache Zeilen um, sofern nicht da (für LoadRechnung relevant)
        zahlungsdaten.zahlungsbedingungen.forEach((item) => (item._zbEinfach = true))
    }

    const content = useMemo(
        () => (
            <>
                {FeatureService.isFeatureActive("DEMO") && <Components.Teaser />}
                {FeatureService.isFeatureActive("UPLOAD") && (
                    <Components.Row>
                        <VorlageUploadView />
                    </Components.Row>
                )}
            </>
        ),
        [t],
    )

    const scrollbuttons = useMemo(
        () => (
            <Components.Row>
                <Components.Button id="top" className={"back-to top"} style={Components.ButtonStyle.LIGHT} label="" onClick={onScrollToTopClicked}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-chevron-double-up" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M7.646 2.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 3.707 2.354 9.354a.5.5 0 1 1-.708-.708l6-6z" />
                        <path fillRule="evenodd" d="M7.646 6.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 7.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z" />
                    </svg>
                </Components.Button>
                <Components.Button id="bottom" className={"back-to bottom"} style={Components.ButtonStyle.LIGHT} label="" onClick={onScrollToBottomClicked}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="currentColor"
                        className="bi bi-chevron-double-down"
                        viewBox="0 0 16 16"
                    >
                        <path
                            fillRule="evenodd"
                            d="M1.646 6.646a.5.5 0 0 1 .708 0L8 12.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
                        />
                        <path
                            fillRule="evenodd"
                            d="M1.646 2.646a.5.5 0 0 1 .708 0L8 8.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
                        />
                    </svg>
                </Components.Button>
            </Components.Row>
        ),
        [onScrollToTopClicked, onScrollToBottomClicked],
    )

    useEffect(() => {
        const mapFieldIdToRechnungPath: Record<string, string | undefined> = {
            "bt-10": "rechnungsdaten.leitwegId",
            "bt-49": "rechnungsempfaenger.empfaenger.eRechnungsAdresse",
        }
        customConfig?.fields?.forEach((field) => {
            if (!field.id || !field.defaultValue || field.id == "steuerberechnung") return
            const path = mapFieldIdToRechnungPath[field.id]
            if (!path) {
                console.error(`no path defined for field customization with id '${field.id}'`)
                return
            }
            const translated = t(field.defaultValue)
            EventBus.instance.emit("changeProperty", path, translated)
        })
    }, [t, customConfig])

    return (
        <div>
            <Components.Header />
            <Components.Container>
                {content}

                <RechnungsdatenView />

                {!FeatureService.isFeatureActive("DEMO") && <RechnungsverlaufView />}

                <RechnungsstellerView basePath="rechnungssteller" />

                <RechnungsstellerKontaktdatenView basePath="rechnungssteller.rechnungssteller.kontaktdaten" />

                <RechnungsempfaengerView basePath="rechnungsempfaenger.empfaenger" />

                {!FeatureService.isFeatureActive("DEMO") && <SteuervertreterVerkaeufersView basePath="steuervertreter" />}

                <LieferinformationView basePath="lieferinformation" />

                <PositionListView path={"positionen"} name={t("rechnung.positionen.title")} />

                <SteuerDetailsView />
                <GesamtsummenView />

                <ZahlungsbedingungenKomplexView />

                <ZahlungsdatenView />

                <AnlagenView />

                <Components.Row>
                    <Components.Col>
                        <p className="small">{t("general.additional.hints.one")}</p>
                        <p className="small">{t("general.additional.hints.five")}</p>
                        <p className="small">{t("general.additional.hints.two")}</p>
                        <p className="small">{t("general.additional.hints.three")}</p>
                        <p className="small">{t("general.additional.hints.four")}</p>
                        {CONFIG?.additionalHints &&
                            CONFIG?.additionalHints.map((hint, idx) => (
                                <p className="small" key={idx}>
                                    {t(hint)}
                                </p>
                            ))}
                    </Components.Col>
                </Components.Row>

                <RechnungErstellenView />
                {scrollbuttons}
            </Components.Container>

            <Components.Footer />
        </div>
    )
}
